import React, { useState, useEffect, useRef, useCallback } from 'react';
import Fuse from 'fuse.js';
import type { ModuleKey } from './layout/AppLayout';
import { loadMandanten }     from '../services/mandantenService';
import { loadFaelligkeiten }  from '../services/vorsorgeService';
import { loadRechnungen }    from '../services/rechnungenService';
import { loadAkte }          from '../services/akteService';
import { loadProtokolle }    from '../services/protokollService';
import { useFocusStore }     from '../store/focusStore';
import { sonderKatName }     from '../lib/dokKategorien';

interface SearchItem {
  id:      string;
  label:   string;
  sub?:    string;
  icon:    string;
  module:  ModuleKey;
  refId?:  string;   // konkreter Eintrag (Deep-Link via focusStore)
}

interface Props {
  navigate: (m: ModuleKey) => void;
  onClose:  () => void;
}

const AKTE_ICON: Record<string, string> = {
  DOKUMENT: '📄', BELEG: '🧾', EMAIL: '✉', BESPRECHUNG: '🗒',
};

async function buildIndex(): Promise<SearchItem[]> {
  const [mandanten, faelligkeiten, rechnungen, akte, protokolle] = await Promise.all([
    loadMandanten(),
    loadFaelligkeiten(),
    loadRechnungen(),
    loadAkte(),
    loadProtokolle(),
  ]);
  const firmaOf = (id?: string): string =>
    (id !== undefined && id !== '' ? (sonderKatName(id) ?? mandanten.find(m => m.id === id)?.name ?? id) : '—');

  const items: SearchItem[] = [
    // Module-Shortcuts
    { id: 'nav-dashboard',    label: 'Dashboard',        icon: '⊞', module: 'dashboard'    },
    { id: 'nav-vorsorge',     label: 'Vorsorge',         icon: '🩺', module: 'vorsorge'     },
    { id: 'nav-wietz',        label: 'WIETZ',            icon: '📋', module: 'wietz'        },
    { id: 'nav-kalender',     label: 'Kalender',         icon: '📅', module: 'kalender'     },
    { id: 'nav-zeit',         label: 'Zeit & Leistung',  icon: '⏱',  module: 'zeit'         },
    { id: 'nav-abrechnung',   label: 'Abrechnung',       icon: '💶', module: 'abrechnung'   },
    { id: 'nav-email',        label: 'E-Mail',           icon: '✉',  module: 'email'        },
    { id: 'nav-formulare',    label: 'Formulare',        icon: '📄', module: 'formulare'    },
    { id: 'nav-reporting',    label: 'GF-Reporting',     icon: '📊', module: 'reporting'    },
    { id: 'nav-asa',          label: 'ASA / GBU',        icon: '🛡',  module: 'asa'          },
    { id: 'nav-einstellungen',label: 'Einstellungen',    icon: '⚙',  module: 'einstellungen'},
  ];

  for (const m of mandanten) {
    items.push({
      id:     `mandant-${m.id}`,
      label:  m.name,
      sub:    m.kurz_code + (m.ort ? ` · ${m.ort}` : ''),
      icon:   '🏢',
      module: 'einstellungen',
    });
  }

  for (const f of faelligkeiten) {
    if (f.status === 'ERLEDIGT') continue;
    items.push({
      id:     `vgu-${f.id}`,
      label:  `VGÜ ${f.tatbestandCode}`,
      sub:    `${f.mitarbeiterKennung} · fällig ${f.faelligkeitDatum}`,
      icon:   f.status === 'UEBERFAELLIG' ? '🔴' : '🟡',
      module: 'vorsorge',
    });
  }

  for (const r of rechnungen) {
    items.push({
      id:     `rechnung-${r.rechnungsNr}`,
      label:  r.rechnungsNr,
      sub:    r.empfaenger.firma + ` · ${r.status} · ${r.datum.slice(0, 10)}`,
      icon:   '💶',
      module: 'abrechnung',
    });
  }

  // Dokumente / Akte-Einträge (zentral durchsuchbar)
  for (const e of akte) {
    items.push({
      id:     `akte-${e.id}`,
      label:  e.titel,
      sub:    `${firmaOf(e.mandant_id)} · ${e.typ} · ${e.datum.slice(0, 10)}`
              + (e.anhang_b64 !== undefined ? ' · 📎' : ''),
      icon:   AKTE_ICON[e.typ] ?? '📄',
      module: 'akte',
      refId:  e.id,
    });
  }

  // Besprechungsprotokolle (+ Anhang-Dateinamen im sub → mitdurchsuchbar)
  for (const p of protokolle) {
    const dateien = p.anhaenge.map(a => a.name).join(' ');
    items.push({
      id:     `protokoll-${p.id}`,
      label:  p.titel || 'Besprechung',
      sub:    `${firmaOf(p.mandant_id)} · ${p.datum}${dateien !== '' ? ` · ${dateien}` : ''}`,
      icon:   '📝',
      module: 'protokolle',
      refId:  p.id,
    });
  }

  return items;
}

export function GlobalSearch({ navigate, onClose }: Props): React.JSX.Element {
  const [query,   setQuery]   = useState('');
  const [items,   setItems]   = useState<SearchItem[]>([]);
  const [results, setResults] = useState<SearchItem[]>([]);
  const [cursor,  setCursor]  = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const fuseRef  = useRef<Fuse<SearchItem> | null>(null);

  useEffect(() => {
    void buildIndex().then(data => {
      setItems(data);
      fuseRef.current = new Fuse(data, {
        keys:              ['label', 'sub'],
        threshold:         0.35,
        includeScore:      false,
        minMatchCharLength: 1,
      });
      setResults(data.slice(0, 8));
    });
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!fuseRef.current) return;
    if (query.trim() === '') {
      setResults(items.slice(0, 8));
    } else {
      setResults(fuseRef.current.search(query).slice(0, 8).map(r => r.item));
    }
    setCursor(0);
  }, [query, items]);

  const requestFocus = useFocusStore(s => s.request);
  const select = useCallback((item: SearchItem): void => {
    if (item.refId !== undefined) requestFocus(item.module, item.refId);
    navigate(item.module);
    onClose();
  }, [navigate, onClose, requestFocus]);

  const handleKey = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === 'Enter' && results[cursor] !== undefined) select(results[cursor]);
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] bg-black/40 backdrop-blur-sm"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={e => { e.stopPropagation(); }}>
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <span className="text-gray-400 text-lg flex-shrink-0">🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); }}
            onKeyDown={handleKey}
            placeholder="Suchen — Module, Mandanten, Rechnungen, Vorsorgen…"
            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100
                       placeholder-gray-400 outline-none"
          />
          <kbd className="hidden sm:inline text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <ul className="max-h-80 overflow-y-auto py-1">
          {results.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-gray-400">Keine Ergebnisse</li>
          )}
          {results.map((item, i) => (
            <li key={item.id}>
              <button
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors text-sm
                  ${i === cursor
                    ? 'bg-blue-50 dark:bg-blue-950/40'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                onMouseEnter={() => { setCursor(i); }}
                onClick={() => { select(item); }}
              >
                <span className="text-base flex-shrink-0">{item.icon}</span>
                <span className="flex-1 min-w-0">
                  <span className="font-medium text-gray-900 dark:text-gray-100 block truncate">{item.label}</span>
                  {item.sub !== undefined && (
                    <span className="text-xs text-gray-400 block truncate">{item.sub}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800 flex gap-4 text-xs text-gray-400">
          <span>↑↓ navigieren</span>
          <span>↵ öffnen</span>
          <span>Esc schließen</span>
        </div>
      </div>
    </div>
  );
}
